import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { useSummit } from "../pages/summitdetails";
import {
  RiFlaskLine, RiHeartPulseLine, RiVirusLine, RiSyringeLine,
  RiBrainLine, RiCpuLine, RiGlobalLine, RiUserHeartLine,
  RiTestTubeLine, RiMedicineBottleLine, RiHealthBookLine,
  RiMicroscopeLine, RiCalendarLine, RiArrowRightLine,
  RiBarChartBoxLine, RiShieldStarLine, RiTeamLine,
  RiMentalHealthLine, RiFocusLine, RiSettings4Line,
  RiSearchLine, RiCloseLine, RiRefreshLine, RiArrowUpLine,
  RiFilter3Line, RiAddLine, RiInformationLine, RiEyeLine,
  RiArrowDownSLine, RiFullscreenLine
} from 'react-icons/ri';

/* ─── THEME COLORS ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

const iconMap = {
  RiFlaskLine, RiHeartPulseLine, RiVirusLine, RiSyringeLine,
  RiBrainLine, RiCpuLine, RiGlobalLine, RiUserHeartLine,
  RiTestTubeLine, RiMedicineBottleLine, RiHealthBookLine,
  RiMicroscopeLine, RiCalendarLine, RiArrowRightLine,
  RiBarChartBoxLine, RiShieldStarLine, RiTeamLine,
  RiMentalHealthLine, RiFocusLine, RiSettings4Line,
};

const CATEGORY_CONFIG = {
  'basic': { label: 'Basic Science', accent: '#2A9D8F', light: 'rgba(42,157,143,0.12)', text: '#1F7A6E' },
  'clinical': { label: 'Clinical', accent: '#3BBCAE', light: 'rgba(59,188,174,0.12)', text: '#1F7A6E' },
  'therapeutic': { label: 'Therapeutic', accent: '#D4A017', light: 'rgba(212,160,23,0.12)', text: '#B8880F' },
  'infectious': { label: 'Infectious', accent: '#218577', light: 'rgba(33,133,119,0.12)', text: '#1F7A6E' },
  'technology': { label: 'Technology', accent: '#6BC2B5', light: 'rgba(107,194,181,0.12)', text: '#218577' },
  'global': { label: 'Global Health', accent: '#D4A017', light: 'rgba(212,160,23,0.12)', text: '#B8880F' },
};

const TRACK_CONFIG = {
  'plenary': { label: 'Plenary', accent: '#D4A017' },
  'parallel': { label: 'Parallel', accent: '#2A9D8F' },
  'poster': { label: 'Poster', accent: '#3BBCAE' },
  'workshop': { label: 'Workshop', accent: '#6BC2B5' },
};

const ITEMS_PER_PAGE = 12;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Responsive */
  @media(max-width:768px){
    .t-topics-grid{grid-template-columns:repeat(auto-fill,minmax(280px,1fr))!important}
    .t-filters-wrap{flex-direction:column!important}
    .t-filter-buttons{flex-wrap:wrap!important;gap:6px!important}
    .t-search-wrap{width:100%!important}
    .t-stats-bar{gap:10px!important}
    .t-stat-card{padding:12px 16px!important;min-width:80px!important}
    .t-cta-content{flex-direction:column!important;text-align:center!important}
    .t-cta-buttons{width:100%!important;justify-content:center!important}
  }

  @media(max-width:480px){
    .t-topics-grid{grid-template-columns:1fr!important}
    .t-stat-card{padding:10px 12px!important}
    .t-stat-value{font-size:22px!important}
    .t-stat-label{font-size:10px!important}
    .t-filter-buttons button{font-size:11px!important;padding:4px 10px!important}
    .t-search-wrap input{font-size:13px!important;height:34px!important}
    .t-cta{padding:24px 20px!important}
    .t-cta-btn{width:100%!important;justify-content:center!important}
  }

  @media(max-width:360px){
    .t-topics-grid{grid-template-columns:1fr!important}
    .t-stat-card{min-width:70px!important}
    .t-stat-value{font-size:18px!important}
    .t-filter-buttons button{font-size:10px!important;padding:3px 8px!important}
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    padding: 20px;
  }
  .modal-content {
    background: ${T.white};
    max-width: 600px;
    width: 100%;
    border-radius: 20px;
    box-shadow: ${T.shadowLg};
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
    padding: 0;
  }
  .modal-content::-webkit-scrollbar { width: 4px; }
  .modal-content::-webkit-scrollbar-track { background: ${T.borderLight}; }
  .modal-content::-webkit-scrollbar-thumb { background: ${T.primaryLight}; border-radius: 4px; }
  .modal-header {
    padding: 24px 28px 16px;
    border-bottom: 1px solid ${T.borderLight};
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  .modal-body {
    padding: 24px 28px 32px;
  }
  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: ${T.textMuted};
    padding: 4px;
    border-radius: 50%;
    transition: 0.2s;
  }
  .modal-close:hover {
    background: ${T.offWhite};
    color: ${T.text};
  }
`;

const Topics = () => {
  const { eventId } = useSummit();

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTrack, setFilterTrack] = useState('all');
  const [page, setPage] = useState(1);
  const [showBtt, setShowBtt] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Modal state
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchActiveTopics();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const fetchActiveTopics = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `/api/topics/active/${eventId}`;
      console.log("🔍 Fetching topics from:", url);
      console.log("📌 Event ID:", eventId);

      const response = await axios.get(url);

      console.log("📦 Response:", response.data);

      if (response.data.success && response.data.topics) {
        const activeTopics = response.data.topics.filter(t => t.isActive === true);
        setTopics(activeTopics.sort((a, b) => a.order - b.order));
        setError(null);
        console.log(`✅ Loaded ${activeTopics.length} topics`);
      } else {
        setError(response.data.message || "No active topics found for this event");
        setTopics([]);
      }
    } catch (err) {
      console.error("❌ Error fetching topics:", err);
      console.error("❌ Error details:", err.response?.data);
      
      if (err.response?.status === 404) {
        setError("Topics not available for this event yet.");
      } else {
        setError(err.response?.data?.message || "Failed to load topics");
      }
      toast.error("Failed to load topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = [...new Set(topics.map(t => t.category).filter(Boolean))];
    return ['all', ...cats];
  }, [topics]);

  const tracks = useMemo(() => {
    const trks = [...new Set(topics.map(t => t.track).filter(Boolean))];
    return ['all', ...trks];
  }, [topics]);

  const filteredTopics = useMemo(() => {
    let filtered = [...topics];
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(t => t.category === filterCategory);
    }
    if (filterTrack !== 'all') {
      filtered = filtered.filter(t => t.track === filterTrack);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(q) || 
        t.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [topics, filterCategory, filterTrack, search]);

  const paginatedTopics = filteredTopics.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredTopics.length / ITEMS_PER_PAGE);

  const getIconComponent = (iconName) => {
    const Icon = iconMap[iconName] || RiFlaskLine;
    return Icon;
  };

  const getCategoryInfo = (category) => {
    return CATEGORY_CONFIG[category] || { label: 'Research', accent: '#2A9D8F', light: 'rgba(42,157,143,0.12)', text: '#1F7A6E' };
  };

  const getTrackInfo = (track) => {
    return TRACK_CONFIG[track] || { label: 'General', accent: '#6B8090' };
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterTrack('all');
    setSearch('');
    setPage(1);
    setMobileFiltersOpen(false);
  };

  const openModal = (topic) => {
    setSelectedTopic(topic);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTopic(null);
    document.body.style.overflow = '';
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <section style={{ padding: 'clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 12px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 'clamp(10px,0.8vw,11px)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <RiMicroscopeLine size={12} style={{ display: 'inline', marginRight: 5 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.6rem,4.5vw,3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 10 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.85rem,1.3vw,1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
              Loading topics...
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>
        <div style={{ textAlign: "center", padding: "clamp(40px,6vw,60px) 20px" }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: T.textMuted, fontSize: 'clamp(14px,1.2vw,15px)' }}>Loading topics...</p>
        </div>
      </div>
    );
  }

  // ── Error / empty state ──
  if (error && !loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <section style={{ padding: 'clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 12px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 'clamp(10px,0.8vw,11px)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <RiMicroscopeLine size={12} style={{ display: 'inline', marginRight: 5 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.6rem,4.5vw,3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 10 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.85rem,1.3vw,1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
              The congress will provide a comprehensive platform for experts to share knowledge and collaborate. Choose from the following topics for your abstract.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(12px,4vw,20px) clamp(32px,5vw,60px)' }}>
          <div style={{ textAlign: 'center', padding: 'clamp(40px,6vw,80px) 20px', background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>
            <div style={{ width: 'clamp(48px,6vw,60px)', height: 'clamp(48px,6vw,60px)', borderRadius: 14, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <RiSearchLine size={24} color={T.primary} />
            </div>
            <h3 style={{ fontSize: 'clamp(18px,2vw,20px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>No Topics Available</h3>
            <p style={{ color: T.textMuted, maxWidth: 400, margin: '0 auto', fontSize: 'clamp(13px,1.2vw,14px)' }}>{error}</p>
            <button 
              onClick={fetchActiveTopics} 
              style={{ 
                marginTop: 20, 
                padding: 'clamp(8px,1vw,10px) clamp(18px,2vw,24px)', 
                borderRadius: 8, 
                background: T.gradGold, 
                color: T.navyDeep, 
                border: 'none', 
                cursor: 'pointer', 
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 'clamp(13px,1.1vw,14px)'
              }}
            >
              <RiRefreshLine size={15} /> Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <>
      <style>{css}</style>
      <Toaster position="top-right" />

      <div style={{ minHeight: '100vh', background: T.offWhite }}>

        {/* HERO SECTION */}
        <section style={{ padding: 'clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 12px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 'clamp(10px,0.8vw,11px)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <RiMicroscopeLine size={12} style={{ display: 'inline', marginRight: 5 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.6rem,4.5vw,3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 10 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.85rem,1.3vw,1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
              The congress will provide a comprehensive platform for experts to share knowledge and collaborate. Choose from the following topics for your abstract.
            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* STATS BAR */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(16px,3vw,24px) clamp(12px,4vw,40px) 0' }}>
          <div className="t-stats-bar" style={{ display: 'flex', gap: 'clamp(8px,1.5vw,16px)', flexWrap: 'wrap', marginBottom: 'clamp(16px,2.5vw,24px)' }}>
            <div className="t-stat-card" style={{ background: T.white, borderRadius: 10, padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 'clamp(80px,15vw,120px)', boxShadow: T.shadow }}>
              <div style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.accentGold }}>{topics.length}</div>
              <div style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Active Topics</div>
            </div>
            <div className="t-stat-card" style={{ background: T.white, borderRadius: 10, padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 'clamp(80px,15vw,120px)', boxShadow: T.shadow }}>
              <div style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.primary }}>{categories.length - 1}</div>
              <div style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Categories</div>
            </div>
            <div className="t-stat-card" style={{ background: T.white, borderRadius: 10, padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 'clamp(80px,15vw,120px)', boxShadow: T.shadow }}>
              <div style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.accentTealLight }}>{tracks.length - 1}</div>
              <div style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Tracks</div>
            </div>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,4vw,40px)' }}>
          <div style={{ background: T.white, borderRadius: 14, border: `1px solid ${T.border}`, padding: 'clamp(14px,2.5vw,20px)', marginBottom: 'clamp(20px,3vw,32px)', boxShadow: T.shadow }}>
            
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              style={{
                display: 'none',
                width: '100%',
                padding: '10px',
                background: T.softTeal,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                fontSize: 'clamp(13px,1.1vw,14px)',
                fontWeight: 600,
                color: T.text,
                cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: mobileFiltersOpen ? '12px' : 0
              }}
              className="t-filter-toggle"
            >
              <span>Filters & Search</span>
              <span>{mobileFiltersOpen ? '▲' : '▼'}</span>
            </button>

            <div className="t-filters-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(10px,1.5vw,16px)', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="t-filter-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(4px,0.8vw,8px)' }}>
                <button
                  onClick={() => { setFilterCategory('all'); setPage(1); }}
                  style={{
                    padding: 'clamp(4px,0.6vw,6px) clamp(10px,1.5vw,16px)',
                    borderRadius: 30,
                    fontSize: 'clamp(11px,1vw,13px)',
                    fontWeight: 500,
                    border: `1px solid ${filterCategory === 'all' ? T.primary : T.border}`,
                    background: filterCategory === 'all' ? T.primary : T.white,
                    color: filterCategory === 'all' ? T.white : T.textMid,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  All Categories ({topics.length})
                </button>
                {categories.slice(1).map(cat => {
                  const cfg = getCategoryInfo(cat);
                  const count = topics.filter(t => t.category === cat).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => { setFilterCategory(cat); setPage(1); }}
                      style={{
                        padding: 'clamp(4px,0.6vw,6px) clamp(10px,1.5vw,16px)',
                        borderRadius: 30,
                        fontSize: 'clamp(11px,1vw,13px)',
                        fontWeight: 500,
                        border: `1px solid ${filterCategory === cat ? cfg.accent : T.border}`,
                        background: filterCategory === cat ? cfg.light : T.white,
                        color: filterCategory === cat ? cfg.text : T.textMid,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {cfg.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(10px,1.5vw,16px)', marginTop: 'clamp(12px,2vw,16px)', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(4px,0.8vw,8px)', alignItems: 'center' }}>
                <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>Track:</span>
                <button
                  onClick={() => { setFilterTrack('all'); setPage(1); }}
                  style={{
                    padding: 'clamp(3px,0.5vw,4px) clamp(8px,1.2vw,12px)',
                    borderRadius: 20,
                    fontSize: 'clamp(10px,0.9vw,12px)',
                    border: `1px solid ${filterTrack === 'all' ? T.primary : T.border}`,
                    background: filterTrack === 'all' ? T.primary : T.white,
                    color: filterTrack === 'all' ? T.white : T.textMid,
                    cursor: 'pointer'
                  }}
                >
                  All
                </button>
                {tracks.slice(1).map(tr => {
                  const trInfo = getTrackInfo(tr);
                  return (
                    <button
                      key={tr}
                      onClick={() => { setFilterTrack(tr); setPage(1); }}
                      style={{
                        padding: 'clamp(3px,0.5vw,4px) clamp(8px,1.2vw,12px)',
                        borderRadius: 20,
                        fontSize: 'clamp(10px,0.9vw,12px)',
                        border: `1px solid ${filterTrack === tr ? trInfo.accent : T.border}`,
                        background: filterTrack === tr ? `${trInfo.accent}15` : T.white,
                        color: filterTrack === tr ? trInfo.accent : T.textMid,
                        cursor: 'pointer'
                      }}
                    >
                      {trInfo.label}
                    </button>
                  );
                })}
              </div>

              <div className="t-search-wrap" style={{ position: 'relative', width: 'min(280px, 100%)' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.textMuted }}>
                  <RiSearchLine size={12} />
                </span>
                <input
                  type="text"
                  placeholder="Search topics..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{
                    width: '100%',
                    height: 'clamp(34px,4vw,38px)',
                    padding: '0 32px',
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    background: T.white,
                    fontSize: 'clamp(12px,1vw,13px)',
                    outline: 'none',
                    transition: 'all 0.2s',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onFocus={e => { e.target.style.borderColor = T.primary; e.target.style.boxShadow = `0 0 0 2px ${T.primary}20`; }}
                  onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
                />
                {search && (
                  <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', background: 'none', border: 'none', color: T.textMuted }}>
                    <RiCloseLine size={12} />
                  </button>
                )}
              </div>
            </div>

            {(filterCategory !== 'all' || filterTrack !== 'all' || search) && (
              <div style={{ marginTop: 'clamp(10px,1.5vw,16px)' }}>
                <button onClick={clearFilters} style={{ padding: 'clamp(4px,0.5vw,5px) clamp(10px,1.5vw,12px)', borderRadius: 20, fontSize: 'clamp(10px,0.8vw,11px)', background: `${T.accentGold}20`, color: T.accentGoldDark, border: 'none', cursor: 'pointer' }}>
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* TOPICS CARD GRID */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,4vw,40px) clamp(32px,5vw,60px)' }}>
          {filteredTopics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'clamp(40px,6vw,80px) 20px', background: T.white, borderRadius: 16, border: `1px solid ${T.border}` }}>
              <div style={{ width: 'clamp(48px,6vw,60px)', height: 'clamp(48px,6vw,60px)', borderRadius: 14, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <RiSearchLine size={24} color={T.primary} />
              </div>
              <h3 style={{ fontSize: 'clamp(18px,2vw,20px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>No topics found</h3>
              <p style={{ color: T.textMuted, fontSize: 'clamp(13px,1.2vw,14px)' }}>Try adjusting your search or filter criteria</p>
              <button onClick={clearFilters} style={{ marginTop: 20, padding: 'clamp(8px,1vw,10px) clamp(18px,2vw,24px)', borderRadius: 8, background: T.gradGold, color: T.navyDeep, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 'clamp(13px,1.1vw,14px)' }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="t-topics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'clamp(16px,2vw,24px)' }}>
                {paginatedTopics.map((topic, index) => {
                  const Icon = getIconComponent(topic.icon);
                  const categoryInfo = getCategoryInfo(topic.category);
                  const trackInfo = getTrackInfo(topic.track);
                  
                  return (
                    <motion.div
                      key={topic._id || topic.id || `topic-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => openModal(topic)}
                      style={{
                        background: T.white,
                        borderRadius: 16,
                        border: `1px solid ${T.border}`,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                        boxShadow: T.shadow
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowMd}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = T.shadow}
                    >
                      <div style={{ height: 4, background: categoryInfo.accent }} />
                      <div style={{ padding: 'clamp(16px,2.5vw,24px)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                          <div style={{
                            width: 'clamp(44px,5vw,56px)',
                            height: 'clamp(44px,5vw,56px)',
                            borderRadius: 12,
                            background: categoryInfo.light,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'clamp(22px,2.5vw,28px)',
                            color: categoryInfo.accent,
                            flexShrink: 0
                          }}>
                            <Icon size={24} />
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: 20,
                              fontSize: 'clamp(9px,0.7vw,10px)',
                              fontWeight: 600,
                              background: categoryInfo.light,
                              color: categoryInfo.text
                            }}>
                              {categoryInfo.label}
                            </span>
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: 20,
                              fontSize: 'clamp(9px,0.7vw,10px)',
                              fontWeight: 600,
                              background: `${trackInfo.accent}15`,
                              color: trackInfo.accent
                            }}>
                              {trackInfo.label}
                            </span>
                          </div>
                        </div>
                        
                        <h3 style={{ fontSize: 'clamp(16px,1.6vw,18px)', fontWeight: 800, color: T.navy, marginBottom: 8, lineHeight: 1.3 }}>
                          {topic.name}
                        </h3>
                        
                        <p className="line-clamp-3" style={{ fontSize: 'clamp(12px,1.1vw,13px)', color: T.textMid, lineHeight: 1.6, marginBottom: 16 }}>
                          {topic.description}
                        </p>
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${T.borderLight}`, flexWrap: 'wrap', gap: 6 }}>
                          <span style={{ fontSize: 'clamp(10px,0.8vw,11px)', color: T.textMuted }}>
                            Session: {topic.sessionType || 'Oral'}
                          </span>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 'clamp(11px,0.9vw,12px)',
                            fontWeight: 600,
                            color: T.primary,
                          }}>
                            <RiArrowRightLine size={14} /> Read More
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(4px,0.6vw,6px)', marginTop: 'clamp(32px,4vw,48px)', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      minWidth: 'clamp(32px,3vw,36px)',
                      height: 'clamp(32px,3vw,36px)',
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.white,
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <RiArrowRightLine size={12} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        style={{
                          minWidth: 'clamp(32px,3vw,36px)',
                          height: 'clamp(32px,3vw,36px)',
                          borderRadius: 8,
                          border: `1px solid ${page === pageNum ? T.accentGold : T.border}`,
                          background: page === pageNum ? T.gradGold : T.white,
                          color: page === pageNum ? T.navyDeep : T.textMid,
                          fontWeight: page === pageNum ? 600 : 400,
                          cursor: 'pointer',
                          fontSize: 'clamp(12px,1vw,14px)'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      minWidth: 'clamp(32px,3vw,36px)',
                      height: 'clamp(32px,3vw,36px)',
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.white,
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      opacity: page === totalPages ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <RiArrowRightLine size={12} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* CTA SECTION */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,4vw,40px) ' + 'clamp(32px,5vw,60px)' }}>
          <div className="t-cta" style={{
            background: T.gradNavy,
            borderRadius: 20,
            padding: 'clamp(24px,4vw,48px) clamp(20px,4vw,56px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'clamp(20px,3vw,32px)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', width: 300, height: 300, background: 'radial-gradient(circle, rgba(212,160,23,0.1), transparent)', borderRadius: '50%', top: -100, right: -80, pointerEvents: 'none' }} />
            <div className="t-cta-content" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.25)', color: T.accentGoldLight, fontSize: 'clamp(10px,0.8vw,11px)', fontWeight: 700, marginBottom: 10 }}>
                Abstract Submission
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.2rem,2.5vw,2rem)', fontWeight: 900, color: T.white, marginBottom: 6 }}>
                Ready to submit your research?
              </h2>
              <p style={{ fontSize: 'clamp(13px,1.2vw,14px)', color: T.textMuted }}>Join researchers from 60+ countries presenting at the summit.</p>
            </div>
            <div className="t-cta-buttons" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', width: 'auto' }}>
              <Link to="/abstract-submission" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: 'clamp(10px,1.5vw,12px) clamp(20px,2.5vw,28px)',
                background: T.gradGold,
                color: T.navyDeep,
                borderRadius: 50,
                textDecoration: 'none',
                fontSize: 'clamp(13px,1.1vw,14px)',
                fontWeight: 700,
                transition: 'all 0.2s',
                boxShadow: T.shadowGold,
                width: '100%',
                justifyContent: 'center'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowLg; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                Submit Your Abstract
                <RiArrowRightLine size={14} />
              </Link>
              <div style={{ fontSize: 'clamp(10px,0.8vw,11px)', color: T.textMuted, textAlign: 'center' }}>Deadline: March 15, 2025</div>
            </div>
          </div>
        </div>

        {/* BACK TO TOP */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              width: 'clamp(38px,4vw,42px)',
              height: 'clamp(38px,4vw,42px)',
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={16} />
          </button>
        )}
      </div>

      {/* ─── TOPIC DETAIL MODAL ─── */}
      <AnimatePresence>
        {showModal && selectedTopic && (
          <div className="modal-overlay" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.25 }}
              className="modal-content"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: getCategoryInfo(selectedTopic.category).light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: getCategoryInfo(selectedTopic.category).accent
                  }}>
                    {React.createElement(getIconComponent(selectedTopic.icon), { size: 24 })}
                  </div>
                  <div>
                    <h3 style={{ fontSize: 'clamp(18px,2vw,22px)', fontWeight: 800, color: T.navy, margin: 0, lineHeight: 1.2 }}>
                      {selectedTopic.name}
                    </h3>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '11px', fontWeight: 600, background: getCategoryInfo(selectedTopic.category).light, color: getCategoryInfo(selectedTopic.category).text }}>
                        {getCategoryInfo(selectedTopic.category).label}
                      </span>
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '11px', fontWeight: 600, background: `${getTrackInfo(selectedTopic.track).accent}15`, color: getTrackInfo(selectedTopic.track).accent }}>
                        {getTrackInfo(selectedTopic.track).label}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={closeModal} className="modal-close">
                  <RiCloseLine size={24} />
                </button>
              </div>

              <div className="modal-body">
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 'clamp(13px,1.1vw,14px)', fontWeight: 600, color: T.textMid, marginBottom: 6 }}>Description</div>
                  <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', color: T.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {selectedTopic.description}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginTop: 16, borderTop: `1px solid ${T.borderLight}`, paddingTop: 16 }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Session Type</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: T.text, marginTop: 2 }}>{selectedTopic.sessionType || 'Oral'}</div>
                  </div>
                  {selectedTopic.track && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Track</div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: T.text, marginTop: 2 }}>{getTrackInfo(selectedTopic.track).label}</div>
                    </div>
                  )}
                  {selectedTopic.category && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: T.text, marginTop: 2 }}>{getCategoryInfo(selectedTopic.category).label}</div>
                    </div>
                  )}
                  {selectedTopic.order !== undefined && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order</div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: T.text, marginTop: 2 }}>{selectedTopic.order}</div>
                    </div>
                  )}
                </div>

                {selectedTopic.extraInfo && (
                  <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderLight}`, paddingTop: 16 }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Additional Information</div>
                    <p style={{ fontSize: '13px', color: T.textMid }}>{selectedTopic.extraInfo}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Topics;